import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "../api";
import Layout from "../components/Layout";
import Swal from "sweetalert2";
import { buildErrorHtml, isValidDate } from "../utils/validation";

export default function StudentAbsencesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const studentName = location.state?.studentName || "Aluno";

  const [absences, setAbsences] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [editDate, setEditDate] = useState("");
  const [editReason, setEditReason] = useState("");

  const fetchAbsences = useCallback(
    async (pageNumber = 1) => {
      try {
        const response = await api.get(
          `/absences/student/${id}?page=${pageNumber}&limit=9`
        );

        if (Array.isArray(response.data)) {
          setAbsences(response.data);
          setPage(1);
          setTotalPages(1);
          return;
        }

        setAbsences(response.data?.data || []);
        setPage(response.data?.page || 1);
        setTotalPages(response.data?.totalPages || 1);
      } catch {
        Swal.fire("Erro", "Não foi possível carregar as faltas.", "error");
      }
    },
    [id]
  );

  useEffect(() => {
    fetchAbsences(1);
  }, [fetchAbsences]);

  async function handleDelete(absenceId) {
    const confirm = await Swal.fire({
      title: "Excluir falta?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    await api.delete(`/absences/${absenceId}`);

    Swal.fire("Sucesso", "Falta removida.", "success");

    fetchAbsences(page);
  }

  function startEdit(absence) {
    setEditingId(absence.id);

    const formatted = new Date(absence.date).toISOString().split("T")[0];

    setEditDate(formatted);
    setEditReason(absence.reason || "");
  }

  async function saveEdit() {
    try {
      const errors = [];

      if (!isValidDate(editDate)) {
        errors.push("Informe uma data válida.");
      }

      if (errors.length > 0) {
        Swal.fire({
          icon: "warning",
          title: "Campos inválidos",
          html: buildErrorHtml(errors),
        });
        return;
      }

      await api.put(`/absences/${editingId}`, {
        date: editDate,
        reason: editReason,
      });

      Swal.fire("Sucesso", "Falta atualizada.", "success");

      setEditingId(null);
      fetchAbsences(page);
    } catch (error) {
      Swal.fire("Erro", error.response?.data?.error || "Erro ao atualizar.", "error");
    }
  }

  return (
    <Layout>
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Histórico de Faltas - {studentName}</h4>

          <button className="btn btn-secondary" onClick={() => navigate("/dashboard")}>
             Voltar
          </button>
        </div>

        {absences.length === 0 ? (
          <div className="alert alert-info">Nenhuma falta registrada.</div>
        ) : (
          <div className="row">
            {absences.map((absence) => (
              <div key={absence.id} className="col-md-6 col-lg-4 mb-3">
                <div className="card shadow-sm">
                  <div className="card-body">
                    {editingId === absence.id ? (
                      <>
                        <div className="mb-2">
                          <input
                            type="date"
                            className="form-control"
                            value={editDate}
                            onChange={(e) => setEditDate(e.target.value)}
                          />
                        </div>

                        <div className="mb-2">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Motivo"
                            value={editReason}
                            onChange={(e) => setEditReason(e.target.value)}
                          />
                        </div>

                        <div className="d-flex gap-2">
                          <button className="btn btn-success btn-sm" onClick={saveEdit}>
                            Salvar
                          </button>

                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => setEditingId(null)}
                          >
                            Cancelar
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <h6 className="fw-bold">{absence.date.split("-").reverse().join("/")}</h6>

                        <p className="text-muted">{absence.reason || "Sem motivo informado"}</p>

                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => startEdit(absence)}
                          >
                            Editar
                          </button>

                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(absence.id)}
                          >
                            Excluir
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="d-flex justify-content-center align-items-center mt-3">
          <button
            className="btn btn-outline-secondary me-3"
            disabled={page <= 1}
            onClick={() => fetchAbsences(page - 1)}
          >
            Anterior
          </button>

          <span>
            Página {page} de {totalPages}
          </span>

          <button
            className="btn btn-outline-secondary ms-3"
            disabled={page >= totalPages}
            onClick={() => fetchAbsences(page + 1)}
          >
            Próxima
          </button>
        </div>
      </div>
    </Layout>
  );
}

